import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import {
  DateField,
  SelectField,
  TextAreaField,
} from "components/AddVaccineForm/components";
import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { useHealthlinkVaccines } from "features/immunizations/hooks/useHealthlinkVaccines";
import {
  useRecordVaccineRefusal,
  useVaccineRefusalReasons,
} from "features/immunizations/hooks/useVaccineRefusals";
import { onInvalidSubmit } from "utils/reportFormErrors";

import { SearchableSelectField } from "../AddVaccineForm/components/SearchableSelectField";
import s from "./RefusalsTab.module.css";

const schema = z.object({
  vaccineCodeId: z.string().min(1, "Select the vaccine that was not given."),
  refusedOn: z.string().min(1, "Enter the date."),
  refusalReasonCode: z.string().min(1, "Select a reason."),
  notes: z.string().optional(),
});

const toIsoDate = (value) => (value ? String(value).slice(0, 10) : "");

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Record a vaccine that was not given (KIN-1849).
 *
 * The reason list is fetched rather than hardcoded — it is provisional, awaiting clinical
 * sign-off, and a change to it should not need a front-end release. Anything the list does
 * not cover is recorded as OTHER with the detail in notes.
 *
 * `refusalReasonDescription` is never sent: the backend fills it from the code, so the
 * stored wording cannot drift from the reason it belongs to.
 */
export const RecordRefusalDialog = ({ open, onClose, patientId }) => {
  const { data: reasons = [], isLoading: isLoadingReasons } =
    useVaccineRefusalReasons();
  const { data: healthlinkVaccines = [], isLoading: isLoadingVaccines } =
    useHealthlinkVaccines();
  const { mutate: recordRefusal, isPending } = useRecordVaccineRefusal();

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      vaccineCodeId: "",
      refusedOn: today(),
      refusalReasonCode: "",
      notes: "",
    },
  });

  const vaccineOptions = healthlinkVaccines.map((vaccine) => ({
    value: String(vaccine.id),
    label: vaccine.name,
  }));

  const reasonOptions = reasons.map(({ code, description }) => ({
    value: code,
    label: description,
  }));

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleSubmit = (values) => {
    // The name travels with the id because the record has to stay readable on its own:
    // it is what the list and the practice report show, and a catalogue entry that is
    // later withdrawn would otherwise leave a row naming nothing.
    const product = healthlinkVaccines.find(
      (vaccine) => String(vaccine.id) === values.vaccineCodeId
    );

    recordRefusal(
      {
        patientId,
        vaccineCodeId: Number(values.vaccineCodeId),
        vaccineName: product?.name,
        refusedOn: toIsoDate(values.refusedOn),
        refusalReasonCode: values.refusalReasonCode,
        ...(values.notes?.trim() ? { notes: values.notes.trim() } : {}),
      },
      { onSuccess: handleClose }
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Record a vaccine not given"
      width={560}
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit, onInvalidSubmit("RecordRefusalDialog"))} className={s.form}>
          <SearchableSelectField
            name="vaccineCodeId"
            label="Vaccine"
            options={vaccineOptions}
            placeholder={
              isLoadingVaccines ? "Loading vaccines..." : "Search vaccines..."
            }
            disabled={isLoadingVaccines}
            required
            ariaRequired
          />
          <DateField
            name="refusedOn"
            label="Date"
            maxDate={new Date()}
            helperText="The date the decision was made. It cannot be in the future."
            required
            ariaRequired
          />
          <SelectField
            name="refusalReasonCode"
            label="Reason"
            options={reasonOptions}
            disabled={isLoadingReasons}
            placeholder={isLoadingReasons ? "Loading reasons..." : "Select"}
            required
            ariaRequired
          />
          <TextAreaField
            name="notes"
            label="Notes"
            placeholder="Anything the reason list does not cover"
          />

          <div className={s.dialogActions}>
            <Button type="button" variant="outlineGray" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isPending}>
              {isPending ? "Saving..." : "Record"}
            </Button>
          </div>
        </form>
      </FormProvider>
    </Dialog>
  );
};
